/**
 * The support knowledge base's vocabulary and limits — the ONE home for them.
 *
 * The migration (`supabase/migrations/20260922214939_create_support_knowledge_base.sql`)
 * enforces the same statuses, sources, media kinds and length limits with check
 * constraints; change both together. Categories are app-only on purpose: the
 * list grows with the knowledge base, and adding one must not need a migration.
 */

/** The assistant's name — in its prompt, the chat and the escalation summary. */
export const SUPPORT_ASSISTANT_NAME = "Maxnu";

/** Category values double as search terms (`orders_refunds` indexes as "orders refunds"). */
export const SUPPORT_CASE_CATEGORIES = [
  { value: "shows_events", label: "Shows & events" },
  { value: "ticketing_setup", label: "Ticket setup" },
  { value: "seating_switches", label: "Seating & switches" },
  { value: "orders_refunds", label: "Orders, refunds & resends" },
  { value: "box_office", label: "Box office & door" },
  { value: "checkout", label: "Buyer checkout" },
  { value: "payments_payouts", label: "Payments & payouts" },
  { value: "reporting", label: "Reporting & numbers" },
  { value: "access_logins", label: "Access & logins" },
  { value: "venue_settings", label: "Venue settings & features" },
  { value: "messaging", label: "Messaging & email" },
  { value: "artist_pages", label: "Artist pages & tour dates" },
  { value: "onboarding_imports", label: "Onboarding & imports" },
  { value: "fans_data", label: "Fans & exports" },
  { value: "video", label: "Video & specials" },
  { value: "other", label: "Other" },
] as const;

export type SupportCaseCategory =
  (typeof SUPPORT_CASE_CATEGORIES)[number]["value"];

export const SUPPORT_CASE_CATEGORY_VALUES = SUPPORT_CASE_CATEGORIES.map(
  (category) => category.value
) as [SupportCaseCategory, ...SupportCaseCategory[]];

export function supportCaseCategoryLabel(value: string): string {
  return (
    SUPPORT_CASE_CATEGORIES.find((category) => category.value === value)
      ?.label ?? value
  );
}

export const SUPPORT_CASE_STATUSES = [
  "draft",
  "published",
  "archived",
] as const;

export const SUPPORT_CASE_SOURCES = [
  "manual",
  "conversation",
  "seed_docs",
  "seed_slack",
] as const;

export const SUPPORT_MEDIA_KINDS = ["image", "video", "link"] as const;

/** Why an answer got a thumbs-down (mirrored by a check constraint). */
export const SUPPORT_FEEDBACK_REASONS = [
  { value: "incorrect", label: "Wrong" },
  { value: "outdated", label: "Out of date" },
  { value: "wrong_case", label: "Wrong case" },
  { value: "incomplete", label: "Incomplete" },
  { value: "other", label: "Other" },
] as const;

export type SupportFeedbackReason =
  (typeof SUPPORT_FEEDBACK_REASONS)[number]["value"];

export const SUPPORT_CASE_LIMITS = {
  title: { min: 5, max: 200 },
  symptoms: { min: 10, max: 20_000 },
  solution: { min: 10, max: 40_000 },
  mediaDescription: { min: 10, max: 2_000 },
  tagLength: { min: 2, max: 40 },
  maxTags: 20,
  maxMedia: 12,
} as const;

/**
 * A verification older than this reads as "stale". Same window as the
 * ticketing wiki's STALE_VERIFICATION_DAYS, and passed into the
 * `search_support_cases` RPC so the database and the UI agree.
 */
export const SUPPORT_STALE_AFTER_DAYS = 120;

/** Upload allowlist per media kind: mime type → file extension. */
export const SUPPORT_MEDIA_MIME_TYPES = {
  image: {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
  },
  video: {
    "video/mp4": "mp4",
    "video/webm": "webm",
    "video/quicktime": "mov",
  },
} as const;

export type SupportUploadKind = keyof typeof SUPPORT_MEDIA_MIME_TYPES;

/**
 * Per-kind upload ceilings. The bucket's own limit (100 MB, set in the
 * migration) is the hard stop; these keep screenshots small and fail early,
 * before a long upload.
 */
export const SUPPORT_MEDIA_MAX_BYTES: Record<SupportUploadKind, number> = {
  image: 15 * 1024 * 1024,
  video: 100 * 1024 * 1024,
};

export function supportMediaExtension(
  kind: SupportUploadKind,
  mimeType: string
): string | null {
  const allowed: Record<string, string> = SUPPORT_MEDIA_MIME_TYPES[kind];
  return allowed[mimeType] ?? null;
}
